import ViewSubscriptionPlanErrorScreen from '@/epics/subscriptionManagment/pages/viewSubscriptionPlan/viewSubscriptionPlanErrorScreen';
import Button from '@/shared/components/Buttons/Button';
import { ButtonVariant } from '@/shared/components/Buttons/types';
import { Loader } from '@/shared/components/Loader';
import { Tooltip } from '@/shared/components/Tooltip';
import InlineLayout from '@/shared/layout/inlineLayout';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useViewOffer } from '../../hooks';
import ViewOfferContent from './ViewOfferContent';

const EXPIRED_OFFER_EDIT_TOOLTIP = 'Ta oferta wygasła i nie można jej edytować';

const ViewOffer: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const { offer, showErrorScreen, statusLabel, statusBadgeClasses, isLoading } =
    useViewOffer(offerId ?? '');

  if (!offerId) {
    return null;
  }

  const OFFER_NAV_ITEMS = [
    {
      id: 'EdytujOferte',
      label: 'Szczegóły oferty',
      dataTestId: 'Szczegóły oferty',
    },
    {
      id: 'KonfiguracjaIOkresObowiazywania',
      label: 'Konfiguracja i okres obowiązywania',
      dataTestId: 'Konfiguracja i okres obowiązywania',
    },
  ];

  const planHeader = (
    <div className="flex items-start justify-between w-full gap-4">
      <div className="flex min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h2
              className="min-w-0 break-all font-medium text-neutral-10"
              style={{
                fontSize: '18px',
                lineHeight: '28px',
              }}
            >
              {offer?.offerName ?? '—'}
            </h2>
            <span
              className={`px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${statusBadgeClasses.container}`}
              style={{
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              <div
                className={`rounded-full shrink-0 ${statusBadgeClasses.dot}`}
                style={{
                  width: '6px',
                  height: '6px',
                }}
              />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
      {offer?.isExpired ? (
        <Tooltip
          content={EXPIRED_OFFER_EDIT_TOOLTIP}
          delay={300}
          side="bottom"
          forceShow
          triggerWrapContent
          contentMinWidth={280}
          contentMaxWidth={360}
        >
          <Button
            label="Edytuj ofertę"
            variant={ButtonVariant.Primary}
            size="Medium"
            disabled
            onClick={() => {}}
            className="!py-[10px] !px-[16px] !font-medium !text-[14px] shrink-0 !cursor-not-allowed"
            style={{ borderRadius: '8px' }}
          />
        </Tooltip>
      ) : (
        <Button
          label="Edytuj ofertę"
          variant={ButtonVariant.Primary}
          size="Medium"
          onClick={() => navigate(`/Oferty/Edytuj-ofertę/${offerId}`)}
          className="!py-[10px] !px-[16px] !font-medium !text-[14px] shrink-0 hover:cursor-pointer"
          style={{ borderRadius: '8px' }}
        />
      )}
    </div>
  );

  const isContentReady = !isLoading && (offer != null || showErrorScreen);

  if (!isContentReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader text="Wczytywanie szczegółów oferty…" />
      </div>
    );
  }

  if (showErrorScreen) {
    return <ViewSubscriptionPlanErrorScreen />;
  }

  if (!offer) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader text="Wczytywanie szczegółów oferty…" />
      </div>
    );
  }

  return (
    <React.Fragment key={offerId}>
      <InlineLayout
        title="Podgląd oferty"
        navItems={OFFER_NAV_ITEMS}
        contentByNavId={{
          EdytujOferte: (
            <ViewOfferContent activeTabId="EdytujOferte" offer={offer} />
          ),
          KonfiguracjaIOkresObowiazywania: (
            <ViewOfferContent
              activeTabId="KonfiguracjaIOkresObowiazywania"
              offer={offer}
            />
          ),
        }}
        header={planHeader}
        searchParamKey="tab"
        defaultActiveId="EdytujOferte"
      />
    </React.Fragment>
  );
};

export default ViewOffer;
